import React, { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import AlertModal from 'components/core/AlertModal';
import Button from 'components/core/Button';
import ConfirmModal from 'components/core/ConfirmModal';
import EmptyState from 'components/core/EmptyState';
import PageHeader from 'components/core/PageHeader';
import Text from 'components/core/Text';

import CreateGroupModal, { EMPTY_GROUP_FORM, TGroupForm } from 'modals/CreateGroupModal';

import {
  DEFAULT_PLACEMENT,
  getGroup,
  useActivateGroup,
  useCreateGroup,
  useDeleteGroup,
  useDisableGroup,
  useGetGroups,
  usePublishGroup,
  useUpdateGroup,
} from 'api/story';
import { collectFramePlaceholders } from 'helpers/audience';
import { readApiError } from 'helpers/api';
import { fromWireTitle, resolveItemVersions, toSummary, toWireTitle } from 'helpers/story';
import ERROR_CODE from 'constants/errorCodes';
import { isPublishedStatus, TGroupSummary, TStoryItem, TStorySnapshot } from 'types/story';

import GroupCard from './views/GroupCard';
import PublishSummary from './views/PublishSummary';
import {
  StyledBody,
  StyledGrid,
  StyledHeaderActions,
  StyledLoadMore,
  StyledScreen,
} from './GroupsList.styles';

const ACTION_ERROR_COPY: Record<string, string> = {
  [ERROR_CODE.DRAFT_NOT_PUBLISHABLE]:
    'This group needs a cover image and an English title before it can go live.',
  [ERROR_CODE.ACTIVE_GROUP_DELETE]: 'Switch the group off first — a live group can’t be deleted.',
  [ERROR_CODE.LIFECYCLE_UNSUPPORTED]:
    'That switch isn’t available for this group yet — publish it first.',
  [ERROR_CODE.DRAFT_STALE_VERSION]:
    'Someone else saved this group while you had it open. Reload the list and try again.',
  [ERROR_CODE.SCHEDULE_WINDOW_INVALID]: 'The start date has to come before the end date.',
};

const GroupsList = () => {
  const { groups, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useGetGroups();
  const { createGroup } = useCreateGroup();
  const { updateGroup } = useUpdateGroup();
  const { publishGroup } = usePublishGroup();
  const { disableGroup } = useDisableGroup();
  const { activateGroup } = useActivateGroup();
  const { deleteGroup } = useDeleteGroup();
  const navigate = useNavigate();

  const [publishTarget, setPublishTarget] = useState<TGroupSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TGroupSummary | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editState, setEditState] = useState<{
    mode: 'create' | 'edit';
    id?: string;
    form: TGroupForm;
    placeholders: string[];
    version?: number;
    justCreated?: boolean;
  } | null>(null);
  const [draftItems, setDraftItems] = useState<TStoryItem[]>([]);
  // Item versions are derived from what has shipped, so this modal needs the live snapshot even
  // though it only edits metadata — it re-sends the draft's items untouched.
  const [liveSnapshot, setLiveSnapshot] = useState<TStorySnapshot | null>(null);

  const reportError = (error: unknown) => {
    const { errorCode, errorMessage } = readApiError(error);
    setActionError(
      (errorCode ? ACTION_ERROR_COPY[errorCode] : undefined) ??
        errorMessage ??
        'Something went wrong. Please try again.'
    );
  };

  const visibleGroups = useMemo(() => {
    const summaries = groups.map(toSummary);
    return summaries.sort((a, b) => {
      if (a.pinned !== b.pinned) {
        return a.pinned ? -1 : 1;
      }
      return a.displayOrder - b.displayOrder;
    });
  }, [groups]);

  const openStudio = (id: string) =>
    navigate({ to: '/groups/$groupId/studio', params: { groupId: id } });

  const openDetails = async (id: string) => {
    try {
      const detail = await getGroup(id);
      const draft = detail.draftSnapshot;
      const { title, titleTranslations } = fromWireTitle(draft.title);
      setEditState({
        mode: 'edit',
        id,
        version: draft.version,
        form: {
          name: detail.name,
          title,
          titleTranslations,
          coverMediaUrl: draft.coverMediaUrl,
          startsAt: draft.startsAt,
          endsAt: draft.endsAt,
          segmentId: draft.segmentId,
          pinned: draft.pinned,
        },
        placeholders: collectFramePlaceholders(draft.items),
      });
      setDraftItems(draft.items);
      setLiveSnapshot(detail.liveSnapshot);
    } catch (error) {
      reportError(error);
    }
  };

  const toggleActive = async (group: TGroupSummary, active: boolean) => {
    try {
      await (active ? activateGroup(group.id) : disableGroup(group.id));
    } catch (error) {
      reportError(error);
    }
  };

  const confirmPublish = async (group: TGroupSummary) => {
    setPublishTarget(null);
    try {
      await publishGroup(group.id);
    } catch (error) {
      reportError(error);
    }
  };

  const confirmDelete = async (group: TGroupSummary) => {
    setDeleteTarget(null);
    try {
      await deleteGroup(group.id);
    } catch (error) {
      reportError(error);
    }
  };

  const handleSubmit = async (form: TGroupForm) => {
    if (editState?.mode === 'edit' && editState.id && editState.version !== undefined) {
      const { id, justCreated } = editState;
      try {
        await updateGroup({
          id,
          body: {
            name: form.name,
            placement: DEFAULT_PLACEMENT,
            version: editState.version,
            title: toWireTitle(form.title, form.titleTranslations),
            startsAt: form.startsAt ?? null,
            endsAt: form.endsAt ?? null,
            segmentId: form.segmentId ?? null,
            pinned: form.pinned,
            coverMediaUrl: form.coverMediaUrl ?? {},
            items: resolveItemVersions(draftItems, liveSnapshot),
          },
        });
      } catch (error) {
        reportError(error);
        return;
      }
      setEditState(null);
      if (justCreated) {
        openStudio(id);
      }
      return;
    }

    try {
      const detail = await createGroup({ name: form.name, placement: DEFAULT_PLACEMENT });
      const draft = detail.draftSnapshot;
      const { title, titleTranslations } = fromWireTitle(draft.title);
      setEditState({
        mode: 'edit',
        id: detail.id,
        version: draft.version,
        form: {
          name: detail.name,
          title,
          titleTranslations,
          coverMediaUrl: draft.coverMediaUrl,
          startsAt: draft.startsAt,
          endsAt: draft.endsAt,
          segmentId: draft.segmentId,
          pinned: draft.pinned,
        },
        placeholders: collectFramePlaceholders(draft.items),
        justCreated: true,
      });
      setDraftItems(draft.items);
      setLiveSnapshot(null);
    } catch (error) {
      reportError(error);
    }
  };

  const openCreate = () =>
    setEditState({ mode: 'create', form: EMPTY_GROUP_FORM, placeholders: [] });

  return (
    <StyledScreen>
      <PageHeader>
        <Text variant="subtitle" as="h1">
          Stories
        </Text>
        <StyledHeaderActions>
          <Button variant="primary" onClick={openCreate}>
            Create story group
          </Button>
        </StyledHeaderActions>
      </PageHeader>

      <StyledBody>
        {visibleGroups.length === 0 ? (
          <EmptyState
            title={isLoading ? 'Loading…' : 'No story groups yet'}
            description="Create a group, then add slides, cover, schedule and audience in the Studio."
            action={
              <Button variant="primary" onClick={openCreate}>
                Create story group
              </Button>
            }
          />
        ) : (
          <>
            <StyledGrid>
              {visibleGroups.map((group) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  onEditSlides={() => openStudio(group.id)}
                  onEditDetails={() => openDetails(group.id)}
                  onPublish={() => setPublishTarget(group)}
                  onToggleActive={(active) => toggleActive(group, active)}
                  onDelete={() => setDeleteTarget(group)}
                />
              ))}
            </StyledGrid>
            {hasNextPage ? (
              <StyledLoadMore>
                <Button
                  variant="secondary"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}>
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </StyledLoadMore>
            ) : null}
          </>
        )}
      </StyledBody>

      <CreateGroupModal
        isOpen={editState !== null}
        mode={editState?.mode ?? 'create'}
        initialForm={editState?.form ?? EMPTY_GROUP_FORM}
        placeholders={editState?.placeholders ?? []}
        onClose={() => setEditState(null)}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Delete story group"
        message={
          deleteTarget && isPublishedStatus(deleteTarget.status)
            ? 'This group is live. Switch it off first, then delete it.'
            : 'This group and all of its versions will be removed. This can’t be undone.'
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            confirmDelete(deleteTarget);
          }
        }}
      />

      <ConfirmModal
        isOpen={publishTarget !== null}
        title={publishTarget?.pending ? 'Publish changes' : 'Publish story group'}
        message={publishTarget ? <PublishSummary group={publishTarget} /> : ''}
        confirmLabel={publishTarget?.pending ? 'Publish changes' : 'Publish'}
        onClose={() => setPublishTarget(null)}
        onConfirm={() => {
          if (publishTarget) {
            confirmPublish(publishTarget);
          }
        }}
      />

      <AlertModal
        isOpen={actionError !== null}
        title="Couldn’t complete that"
        message={actionError ?? ''}
        onClose={() => setActionError(null)}
      />
    </StyledScreen>
  );
};

export default GroupsList;
